'use client'

import * as React from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { CheckCircleIcon } from 'primitives/icons'
import { VideoIcon } from 'primitives/icons/media-and-devices'
import { ProductType } from 'cf-menu/components/search/search.types'
import { tw } from 'utils/classnames'
import { capitaliseFirstLetter, slugifyCategory } from 'utils/string'
import { Link } from '@repo/web-ui/components/link'
import { getPathnameWithoutLocale } from 'cf-i18n/helpers'
import { Countdown } from '@repo/web-ui/components/countdown'
import { LocalizedPrice } from '@repo/web-ui/components/localized-price/localized-price'
import { usePathname } from 'next/navigation'
import { AppRoute } from 'cf-menu/utils/route'
import { LoadWhenInViewport } from '@repo/web-ui/components/load-when-in-viewport'
import { reactNodeToString } from 'utils/dom'

import { Tooltip } from '../prod/tooltip'
import {
  mapCategoryPageTypeToCategoryDetails,
  STOCKPHOTOS_AUTHOR_ID,
} from '@web/components/content-api/config'
import { Ribbon } from '@web/components/ribbon/ribbon'
import { HoverActions } from '@web/components/hover-actions/hover-actions'
import { InlineStaticVariant } from '@repo/web-ui/components/static-variants-renderer/inline/inline-static-variant'
import {
  HAS_ALL_ACCESS,
  LOGGED_IN,
} from '@repo/web-ui/components/static-variants-renderer/utils/conditions'
import { ProductDownload } from './product-download'
import { useUser } from '@repo/cf-user/hooks/use-user-data'

const ALL_ACCESS_CATEGORIES = [
  'Embroidery',
  'Bundles',
  mapCategoryPageTypeToCategoryDetails.png.authorId,
  STOCKPHOTOS_AUTHOR_ID,
] as const

type ProductCardProps = {
  productId: string
  productUrl: string
  image: string
  imageWidth?: number
  imageHeight?: number
  name: string
  alt?: string
  category: ProductType | string
  categoryText?: string
  categoryUrl?: string
  subcategory?: string
  userHasAccess?: boolean | null
  onClick?: () => void
  extraSettings?: {
    level?: string
    source?: string
  }
  countdown?: number
  isLegacy?: boolean
  className?: string
  imageClassName?: string
  linkInNewTab?: boolean
  hidePriceSection?: boolean
  hoverOverlay?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export function ProductCard({
  productId,
  productUrl,
  image,
  imageWidth = 274,
  imageHeight = 220,
  name,
  alt,
  category,
  categoryText,
  categoryUrl,
  onClick,
  extraSettings,
  countdown,
  isLegacy = false,
  className,
  imageClassName,
  linkInNewTab = false,
  subcategory,
  hidePriceSection,
  userHasAccess: userHasAccessOverride,
  hoverOverlay = false,
  ...restProps
}: ProductCardProps) {
  const t = useTranslations('components.product_card')

  const isSearchPage =
    getPathnameWithoutLocale(usePathname() || '').startsWith(AppRoute.SEARCH) ?? false

  const isClass = category === ProductType.CLASSES
  const isBundle = category === ProductType.BUNDLES

  return (
    <div
      className={tw('group relative flex w-full flex-col rounded shadow-xl @container', className)}
      data-testid="product-card"
      {...(onClick
        ? {
            onClick: () => onClick(),
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onClick()
              }
            },
            role: 'button' as const,
            tabIndex: 0,
          }
        : {})}
      {...restProps}
    >
      {isBundle ? (
        <Ribbon
          text={t('products.bundles')}
          className="rounded-tl scale-[0.7] origin-top-left md:scale-100"
        />
      ) : null}

      <div className="relative flex min-h-0 flex-1">
        <Link
          href={productUrl}
          target={linkInNewTab ? '_blank' : '_self'}
          className="relative inline-block h-full w-full group/image"
        >
          <div className="bg-cf-dreamy-cloud-400 flex h-full w-full justify-center overflow-hidden rounded-t">
            <Image
              src={image}
              alt={alt || name}
              height={imageHeight}
              width={imageWidth}
              unoptimized
              className={tw(
                category === mapCategoryPageTypeToCategoryDetails.png.authorId && 'max-w-[300px]',
                'max-h-[400px] object-contain transition-transform duration-100 ease-in-out',
                'h-auto w-full',
                imageClassName,
              )}
              data-testid="product-card-image"
            />
          </div>
          {isClass && extraSettings ? <ClassSection extraSettings={extraSettings} /> : null}
          {hoverOverlay ? (
            <div className="absolute top-0 left-0 w-full h-full rounded-t bg-cf-grey-800/60 group-hover/image:flex hidden items-center justify-center text-cf-white text-[28px] font-medium">
              {t('open')}
            </div>
          ) : null}
        </Link>

        {countdown !== undefined ? (
          <div className="bg-cf-white absolute bottom-0 right-0 z-[1] rounded-tl px-3 py-[2px]">
            <Countdown date={countdown} prefix={t('countdown_gone_in')} isEmphasized />
          </div>
        ) : null}
      </div>

      <InlineStaticVariant
        conditions={[LOGGED_IN, HAS_ALL_ACCESS]}
        getVariant={React.useCallback(
          ({ conditionsMet }) => (
            <DescriptionSection
              productUrl={productUrl}
              linkInNewTab={linkInNewTab}
              name={name}
              category={category}
              categoryText={categoryText}
              categoryUrl={categoryUrl}
              userHasAccess={conditionsMet ?? userHasAccessOverride}
              subcategory={subcategory}
              hidePriceSection={hidePriceSection}
              productId={productId}
              isLegacy={isLegacy}
              image={image}
            />
          ),
          [
            category,
            categoryText,
            categoryUrl,
            image,
            linkInNewTab,
            name,
            productUrl,
            subcategory,
            userHasAccessOverride,
            hidePriceSection,
            productId,
            isLegacy,
          ],
        )}
      />

      {!isSearchPage ? (
        <HoverActions
          href={productUrl}
          imageSrc={image}
          id={productId}
          title={name}
          isLegacy={isLegacy}
          topOffset={isClass || isBundle ? 40 : undefined}
        />
      ) : null}
    </div>
  )
}

/**
 * Turns a (possibly slugified) category label into spaced, capitalised words so
 * a slug like "ai-generated" never renders as "Ai-generated". Mirrors the
 * `t-shirt` handling in `utils/marketplace-content` so it stays "T-shirt" rather
 * than "T Shirt". This is a workaround for https://creativefabrica.atlassian.net/browse/GROW-2517?focusedCommentId=218014
 */
function deslugifyCategoryLabel(label: string): string {
  return label
    .replace(/t-shirt/gi, 'tshirt')
    .split('-')
    .map(word => (word.toLowerCase() === 'tshirt' ? 'T-shirt' : capitaliseFirstLetter(word)))
    .join(' ')
}

// oxlint-disable-next-line complexity
function DescriptionSection({
  productUrl,
  name,
  category,
  subcategory = '',
  categoryUrl = '',
  categoryText,
  userHasAccess,
  linkInNewTab = false,
  hidePriceSection,
  productId,
  isLegacy,
  image,
}: {
  productUrl: string
  name: string
  category: ProductType | string
  categoryText?: string
  categoryUrl?: string
  subcategory?: string
  userHasAccess?: boolean | null
  linkInNewTab?: boolean
  hidePriceSection?: boolean
  productId: string
  isLegacy: boolean
  image: string
}) {
  const t = useTranslations('components.product_card')
  const { data: user } = useUser()

  const shouldShowDownloadButton =
    user?.subscriptionAccess.allAccess.isSubscribed &&
    user?.subscriptionAccess.allAccess.subscription?.isTrial

  const isClass = category === ProductType.CLASSES
  const isCommunity = category === ProductType.COMMUNITY
  const isGraphics = category === ProductType.GRAPHICS
  const isEmbroidery = category === ProductType.EMBROIDERY
  const isPNG = category === mapCategoryPageTypeToCategoryDetails.png.authorId || category === 'png'

  const categoryHref =
    categoryUrl ||
    // oxlint-disable-next-line no-nested-ternary
    (isEmbroidery
      ? '/subscriptions/embroidery/'
      : isGraphics
        ? `/subscriptions/graphics/${slugifyCategory(subcategory)}/`
        : `/${isPNG ? 'png' : slugifyCategory(category)}/`)

  let categoryLabel = categoryText || capitaliseFirstLetter(category).replace(/-/g, ' ')
  if (isGraphics && subcategory) {
    categoryLabel = capitaliseFirstLetter(subcategory)
  } else if (isPNG) {
    categoryLabel = t('png_category')
  }
  categoryLabel = deslugifyCategoryLabel(categoryLabel)

  const is3dSvg = categoryLabel.toLowerCase() === '3d svg'

  return (
    <div className="bg-cf-white grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 rounded-b bg-white px-2 py-3">
      {!isCommunity ? (
        <div
          className={tw(
            'row-start-1 flex items-center justify-between gap-[10px]',
            userHasAccess ? 'col-span-2' : 'col-span-3',
          )}
        >
          <Link
            href={productUrl}
            color="inherit"
            target={linkInNewTab ? '_blank' : '_self'}
            className="hover:text-secondary-500 overflow-hidden text-ellipsis whitespace-nowrap font-bold no-underline"
            title={name}
            data-testid="product-card-title"
          >
            {name}
          </Link>
        </div>
      ) : null}
      <span
        className={tw(
          'truncate text-xs inline-flex items-center min-h-[26px] overflow-hidden text-ellipsis whitespace-nowrap',
          (!userHasAccess || !isCommunity) && 'col-span-2',
        )}
      >
        {t('in')}
        <Link
          className={tw(
            'hover:text-secondary-500 text-blue-500 no-underline ml-[2px] first-letter:capitalize overflow-hidden text-ellipsis whitespace-nowrap',
            is3dSvg && 'uppercase',
          )}
          target={linkInNewTab ? '_blank' : '_self'}
          href={categoryHref}
          data-testid="product-card-category"
          dangerouslySetInnerHTML={{ __html: categoryLabel }}
        />
      </span>
      {!hidePriceSection ? (
        <div
          className={tw(
            'flex',
            userHasAccess
              ? 'col-start-3 row-start-1 justify-end'
              : tw('col-start-1', isCommunity ? 'row-start-2' : 'row-start-3'),
          )}
        >
          <PriceSection userHasAccess={userHasAccess} category={category} isClass={isClass} />
        </div>
      ) : null}
      {shouldShowDownloadButton ? (
        <ProductDownload
          productId={productId}
          isLegacy={isLegacy}
          productData={{
            category,
            subcategory,
            imageUrl: image,
            name,
          }}
        />
      ) : null}
    </div>
  )
}

function PriceSection({
  userHasAccess,
  category,
  isClass,
}: Pick<ProductCardProps, 'userHasAccess' | 'category'> & {
  isClass: boolean
}) {
  const t = useTranslations('components.product_card')

  const tooltipText = isClass ? t('tooltips.classes') : t('tooltips.product_card')

  // Don't render anything while we're waiting for the userHasAccess value
  if (userHasAccess === null || userHasAccess === undefined) {
    return null
  }

  if (userHasAccess) {
    if (tooltipText) {
      return (
        <LoadWhenInViewport
          className="flex"
          fallback={<CheckCircleIcon className="text-secondary-500 size-5" />}
        >
          <Tooltip
            delay={0}
            sideOffset={5}
            value={tooltipText}
            side="top"
            className="bg-secondary-500 w-[250px]"
            arrowClassName="fill-secondary-500"
          >
            <button>
              <CheckCircleIcon
                className="text-secondary-500 size-5"
                data-testid="product-card-tooltip-icon"
              />
            </button>
          </Tooltip>
        </LoadWhenInViewport>
      )
    }

    return <CheckCircleIcon className="text-secondary-500 size-5" />
  }

  return <ZeroAllAccessPrice />
}

function ZeroAllAccessPrice() {
  const t = useTranslations('components.product_card')
  const text = t.rich('zero_all_access_price', {
    price: () => <LocalizedPrice value={0} />,
  })
  return (
    <span className="truncate text-xs font-bold text-[#0F7B59]" title={reactNodeToString(text)}>
      {text}
    </span>
  )
}

function ClassSection({
  extraSettings,
}: {
  extraSettings: NonNullable<ProductCardProps['extraSettings']>
}) {
  return (
    <>
      <div className="bg-cf-white absolute right-0 top-[5px] flex h-[30px] w-[32px] items-center justify-center rounded-bl rounded-tl">
        <VideoIcon color="#2a1f3f" className="size-4" />
      </div>
      {extraSettings.level ? (
        <div
          className={tw(
            extraSettings.level === 'Beginner' && 'bg-secondary-500 hover:bg-secondary-700',
            extraSettings.level === 'Intermediate' && 'bg-[#f7b500] hover:bg-[#e0a501]',
            extraSettings.level === 'Advanced' && 'bg-red-700 hover:bg-[#a91f42]',
            'text-cf-white absolute bottom-[10px] left-[10px] rounded-[2px] px-2 py-[3px] text-xs font-semibold',
          )}
        >
          {extraSettings.level}
        </div>
      ) : null}

      <div
        className={tw(
          extraSettings.source === 'Sourced' && 'hidden',
          'text-cf-white absolute bottom-[10px] right-[10px] rounded-[2px] bg-[#541156] px-2 py-[3px] text-xs font-semibold hover:bg-[#260427]',
        )}
      >
        {`CF ${extraSettings.source}`}
      </div>
    </>
  )
}

export function ProductCardSkeleton() {
  return (
    <div className="relative aspect-square w-full animate-pulse rounded bg-gray-800">
      <div className="bg-cf-dreamy-cloud-500 absolute bottom-10 left-4 h-[20px] w-1/2 rounded" />
      <div className="bg-cf-dreamy-cloud-500 absolute bottom-3 left-4 h-[20px] w-1/4 rounded" />
    </div>
  )
}
